import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { Observable } from 'rxjs';
import { ConfirmDialogComponent } from 'src/app/shared/confirm-dialog/confirm-dialog.component';
import Swal from 'sweetalert2';

@Injectable({
    providedIn: 'root'
})
export class AlertService {
    private readonly config: MatSnackBarConfig;

    constructor(private snackBar: MatSnackBar, public dlog: MatDialog,) {
        this.config = new MatSnackBarConfig();
        this.config.duration = 4500;
    }

    message(message: string) {
        this.open(message, ['alert-message']);
    }

    success(message: string) {
        this.open(message, ['alert-success']);
    }

    error(message: string, duration = 4500) {
        this.open(message, ['alert-error'], '', duration);
    }

    warning(message: string) {
        this.open(message, ['alert-warning']);
    }

    private open(message: string, classes: Array<string>, action: string = '', duration = 4500) {
        this.config.panelClass = [...classes, 'custom-snackbar'];
        this.config.duration = duration;
        this.snackBar.open(message, action, this.config);
    }

    dialog(title, message): Observable<any> {
        const dialogRef = this.dlog.open(ConfirmDialogComponent, {
            width: '400px',
            data: {
                title,
                message,
                btnOkText: 'Sim',
                btnCancelText: 'Não'
            }
        });
        return dialogRef.afterClosed();
    }

    inputDialog(title, message): Observable<any> {
        return new Observable((observer) => {
            Swal.fire({
                title,
                html: message,
                input: "text",
                inputAttributes: {
                    autocapitalize: "off"
                },
                showCancelButton: true,
                confirmButtonText: "Confirmar",
                showLoaderOnConfirm: true,
                allowOutsideClick: () => !Swal.isLoading()
            }).then((result) => {
                /* Exemplo Resultado
                {
                    isConfirmed: true,
                    isDenied: false,
                    isDismissed: false,
                    value: "string"
                }*/

                // if (result.isConfirmed) {
                //     observer.next(result.value);
                // } 
                observer.next(result);

            }).catch((err) => {
                observer.error(err);
            });
        })
    }

    inputDigitalLine(title, message): Observable<any> {
        return new Observable((observer) => {
            Swal.fire({
                title,
                html: message,
                input: "text",
                inputAttributes: {
                    autocapitalize: "off"
                },
                showCancelButton: true,
                confirmButtonText: "Confirmar",
                showLoaderOnConfirm: true,
                allowOutsideClick: () => !Swal.isLoading(),
                customClass: {
                    popup: 'custom-swal-width'
                },
                preConfirm: (value) => {
                    if (!value) {
                        Swal.showValidationMessage('Por favor, insira uma linha digitável.');
                    }
                    return value;
                }
            }).then((result) => {
                observer.next(result);
            }).catch((err) => {
                observer.error(err);
            });
        });
    }

    inputMessage(title: string, message: string): Observable<any> {
        return new Observable((observer) => {
            Swal.fire({
                title,
                html: message,
                confirmButtonText: "Ok",
                showLoaderOnConfirm: true,
                allowOutsideClick: () => !Swal.isLoading()
            }).then((result) => {
                observer.next(result);
                observer.complete(); // Completa o observable
            }).catch((err) => {
                observer.error(err);
            });
        });
    }

    /**
     * Dialog para confirmar ação
     * @param title Nome do item que será excluído
     * @returns uma Promise
     */
    dialogConfirm(title): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            Swal.fire({
                title,
                showDenyButton: false,
                showCancelButton: false,
                confirmButtonText: 'OK'
            }).then((result) => {
                if (result.isConfirmed) {
                    resolve(true);
                } else if (result.isDenied) {
                    Swal.fire('Ação cancelada', '', 'error');
                    reject(false);
                }
            });
        });
    }

    /**
     * Dialog para confirmar ação
     * @param title Nome do item que será excluído
     * @returns uma Promise
     */
    dialogConfirmYorN(title): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            Swal.fire({
                title,
                showDenyButton: true,
                showCancelButton: false,
                confirmButtonText: 'Sim',
                denyButtonText: `Não`,
                customClass: {
                    popup: 'custom-swal-width', // Adiciona uma classe ao modal
                },
            }).then((result) => {
                if (result.isConfirmed) {
                    resolve(true);
                } else if (result.isDenied) {
                    resolve(false);
                }
            });
        });
    }

    dialogConfirmRegime(title: string): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            Swal.fire({
                title,
                showDenyButton: true,
                showCancelButton: false,
                confirmButtonText: 'Sim',
                denyButtonText: 'Não',
                allowOutsideClick: false,  // Impede clicar fora do modal
                allowEscapeKey: false,     // Impede fechar com ESC
                allowEnterKey: false,       // Impede submeter com Enter
                backdrop: true,            // Fundo escuro que bloqueia interação
                customClass: {
                    popup: 'custom-swal-width',
                },
            }).then((result) => {
                if (result.isConfirmed) {
                    resolve(true);
                } else if (result.isDenied) {
                    resolve(false);
                }
            });
        });
    }

    /**
     * Dialog para confirmar se vai querer alterar o status
     * @param active // Valor do status
     * @param title Nome do item que será excluído
     * @returns uma Promise
     */
    changeStatus(active, title): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            Swal.fire({
                title: `Deseja ${active === true ? 'desativar' : 'ativar'} o <br> ${title}?`,
                showDenyButton: true,
                showCancelButton: false,
                confirmButtonText: 'Sim',
                denyButtonText: `Não`,
            }).then((result) => {
                if (result.isConfirmed) {
                    resolve(true);
                } else if (result.isDenied) {
                    Swal.fire('Ação cancelada', '', 'error');
                    reject(false);
                }
            });
        });
    }
}
